/**
 * Draws a timeline's line in proportion to how far you have scrolled through it,
 * and undraws it as you scroll back up.
 *
 * Markup contract:
 *   <ol data-timeline>
 *     <span data-timeline-progress></span>
 *     <li data-timeline-step> ... <span class="timeline__dot"></span> ... </li>
 *   </ol>
 *
 * Progress runs from the first dot to the last, measured against a line partway
 * down the viewport: 0 when that line sits on the first dot, 1 when it reaches
 * the last. The fill is a single element scaled by that value, so it tracks the
 * scrollbar exactly in both directions rather than snapping between steps.
 *
 * Steps light and unlight as the fill passes their dot.
 *
 * Everything renders fully drawn by default. The `is-revealing` class is only
 * added once this script confirms it can animate, so no JS, an old browser, or
 * reduced motion all leave a complete, readable timeline.
 */
(function () {
  var TRIGGER = 0.55; // fraction of viewport height

  var supported = 'IntersectionObserver' in window && typeof window.requestAnimationFrame === 'function';
  var reduced =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function setup(list) {
    if (list.timelineReady) return;
    list.timelineReady = true;

    var steps = Array.prototype.slice.call(list.querySelectorAll('[data-timeline-step]'));
    var fill = list.querySelector('[data-timeline-progress]');
    if (!steps.length || !fill) return;

    if (!supported || reduced) {
      steps.forEach(function (step) {
        step.classList.add('is-active');
      });
      return;
    }

    list.classList.add('is-revealing');

    var offsets = [];
    var first = 0;
    var span = 0;
    var frame = null;

    // Dot centres relative to the list. Cached, because reading them on every
    // scroll event would force layout on each frame.
    function measure() {
      offsets = steps.map(function (step) {
        var dot = step.querySelector('.timeline__dot');
        var within = dot ? dot.offsetTop + dot.offsetHeight / 2 : 0;
        return step.offsetTop + within;
      });

      first = offsets[0];
      span = offsets[offsets.length - 1] - first;

      fill.style.top = first + 'px';
      fill.style.height = span + 'px';
    }

    function update() {
      frame = null;

      var top = list.getBoundingClientRect().top;
      var trigger = window.innerHeight * TRIGGER;

      // Unclamped, so a step above the line can deactivate on the way back up.
      var raw = span > 0 ? (trigger - (top + first)) / span : 1;
      var clamped = Math.min(1, Math.max(0, raw));

      list.style.setProperty('--tl-progress', clamped);

      steps.forEach(function (step, index) {
        var threshold = span > 0 ? (offsets[index] - first) / span : 0;
        step.classList.toggle('is-active', raw >= threshold);
      });
    }

    function schedule() {
      if (frame === null) frame = window.requestAnimationFrame(update);
    }

    function remeasure() {
      measure();
      schedule();
    }

    measure();
    update();

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', remeasure);

    if ('ResizeObserver' in window) {
      new ResizeObserver(remeasure).observe(list);
    }
  }

  function init(root) {
    var scope = root && root.querySelectorAll ? root : document;
    Array.prototype.forEach.call(scope.querySelectorAll('[data-timeline]'), setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
